import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { ButtonClickedEvent, withAnalytics } from '@atlassian/mpac-analytics';
import { isFreeApp } from '@atlassian/mpac-api-client';
import { WindowUtils } from '@atlassian/mpac-utils';
import { Option } from 'funfix-core';
import React from 'react';
import { makeAnalyticsForInstallableAppIdentifier } from '../../../../analytics/AppId';
import { ANALYTICS_SUBJECT_ID_ALREADY_INSTALLED_CTA, ANALYTICS_SUBJECT_ID_INSTALL } from '../../../../analytics/Events';
import { foldInstallInstallableAppInstallMode } from '../../../../records/InstallMode/InstallInstallableAppInstallMode';
import { isInstallStatusLoading } from '../../../../records/InstallMode/InstallStatus';
import { isInstallableAppCloudFortified } from '../../utils/installableApp';
import ConfirmCancelFooter from './ConfirmCancelFooter';
import { jsx as _jsx } from "react/jsx-runtime";
const MANUAL_URL_PRIMARY_CTA_LABEL = 'Get it now';
const FREE_PRIMARY_CTA_LABEL = 'Get it now';
const PAID_PRIMARY_CTA_LABEL = 'Start free trial';
const ADD_TO_SANDBOX_CTA_LABEL = 'Add to sandbox';
const CLOUD_MIGRATION_TRIAL_CTA_LABEL = 'Activate trial';
const ADD_CTA_LABEL = 'Add';

// eslint-disable-next-line max-lines-per-function
export const InstallableAppFooterBase = props => {
  const installApp = (installableApp, installableAppId, label, relayState) => () => {
    var _props$selectedEditio;
    const ecosystemCloudAppId = installableApp.marketplaceAppDetails.ecosystemCloudAppId;
    const installAppArgs = {
      context: props.context,
      hostConfig: props.hostConfig,
      hostInfo: props.hostInfo,
      installableApp,
      installableAppId,
      relayState
    };
    props.fireAnalyticsEvent(makeAnalyticsForInstallableAppIdentifier(ButtonClickedEvent, ANALYTICS_SUBJECT_ID_INSTALL, installableAppId, _objectSpread(_objectSpread({
      label
    }, ecosystemCloudAppId && {
      isEcosystemCloudApp: true,
      ecosystemCloudAppId
    }), {}, {
      hasCloudFortifiedBadge: isInstallableAppCloudFortified(installableApp)
    }, props.isEditionsSupported && {
      selectedPlan: (_props$selectedEditio = props.selectedEditionType) === null || _props$selectedEditio === void 0 ? void 0 : _props$selectedEditio.value
    })).toObject());
    props.installApp(installAppArgs);
  };
  const onClickAlreadyInstalledCTA = (installableAppId, label, url) => () => {
    props.fireAnalyticsEvent(makeAnalyticsForInstallableAppIdentifier(ButtonClickedEvent, ANALYTICS_SUBJECT_ID_ALREADY_INSTALLED_CTA, installableAppId, {
      label
    }).toObject());
    WindowUtils.openPage(url, '_top');
  };
  const resolveAlreadyInstalledPrimaryCTALink = installedApp => {
    let ctaLink;
    if (installedApp.getStartedUrl) ctaLink = {
      label: 'Get started',
      url: installedApp.getStartedUrl
    };else if (installedApp.configureUrl) ctaLink = {
      label: 'Configure',
      url: installedApp.configureUrl
    };else if (installedApp.manageAppUrl) ctaLink = {
      label: 'Manage app',
      url: installedApp.manageAppUrl
    };
    return Option.of(ctaLink);
  };
  const resolveDefaultPrimaryCTAText = defaultInstallMode => {
    if (props.isEditionsSupported && props.eligibleForFreeOnSandboxWithEditions) {
      return ADD_TO_SANDBOX_CTA_LABEL;
    }
    if (props.isEditionChargedSeparatelyInSandbox) {
      return PAID_PRIMARY_CTA_LABEL;
    }
    if (!props.isEditionsSupported && defaultInstallMode.installableApp.eligibleForFreeOnSandbox) {
      return ADD_TO_SANDBOX_CTA_LABEL;
    }
    if (props.appHasMultiInstanceEntitlement && !props.isEditionsSupported) {
      return ADD_CTA_LABEL;
    }
    if (isFreeApp(defaultInstallMode.installableApp.marketplaceAppDetails.paymentModel)) {
      return FREE_PRIMARY_CTA_LABEL;
    }
    if (defaultInstallMode.installableApp.cloudMigrationTrial.isEligible) {
      return CLOUD_MIGRATION_TRIAL_CTA_LABEL;
    }
    return PAID_PRIMARY_CTA_LABEL;
  };
  const resolveConfirmCancelForAppByKey = (installableAppDefault, isPrimaryDisabled, isInstallInProgress) => {
    const primaryCTALabel = resolveDefaultPrimaryCTAText(installableAppDefault);
    return /*#__PURE__*/_jsx(ConfirmCancelFooter, {
      closeInstallDialog: props.closeInstallDialog,
      isLoading: isInstallInProgress(),
      isPrimaryDisabled: isPrimaryDisabled,
      onPrimaryCTAClick: installApp(installableAppDefault.installableApp, installableAppDefault.installableAppId, primaryCTALabel, installableAppDefault.relayState),
      primaryCTALabel: primaryCTALabel,
      primaryButtonId: "install-app-dialog__install-confirm-btn" // used in pollinator test
    });
  };
  const resolveConfirmCancelForAppByUrl = (installableAppByManualUrl, isPrimaryDisabled) => installableAppByManualUrl.installableApp.flatMap(ia => ia.toOption()).map(ia =>
  /*#__PURE__*/
  // eslint-disable-next-line react/jsx-key
  _jsx(ConfirmCancelFooter, {
    closeInstallDialog: props.closeInstallDialog,
    isLoading: isInstallStatusLoading(installableAppByManualUrl.status),
    isPrimaryDisabled: isPrimaryDisabled,
    onPrimaryCTAClick: installApp(ia, installableAppByManualUrl.installableAppByUrlId, MANUAL_URL_PRIMARY_CTA_LABEL),
    primaryCTALabel: MANUAL_URL_PRIMARY_CTA_LABEL
  }));
  return foldInstallInstallableAppInstallMode(props.installableAppInstallMode)(installableAppDefault => installableAppDefault.installedApp.fold(() => resolveConfirmCancelForAppByKey(installableAppDefault, false, props.isInstallInProgress), ia => resolveAlreadyInstalledPrimaryCTALink(ia).map(cta =>
  /*#__PURE__*/
  // eslint-disable-next-line react/jsx-key
  _jsx(ConfirmCancelFooter, {
    closeInstallDialog: props.closeInstallDialog,
    isLoading: false,
    isPrimaryDisabled: false,
    onPrimaryCTAClick: onClickAlreadyInstalledCTA(installableAppDefault.installableAppId, cta.label, cta.url),
    primaryCTALabel: cta.label
  })).getOrElseL(() => resolveConfirmCancelForAppByKey(installableAppDefault, true, props.isInstallInProgress))), installableAppByManualUrl => installableAppByManualUrl.installableAppByManualUrl.flatMap(iabmu => iabmu.installedApp.fold(() => resolveConfirmCancelForAppByUrl(iabmu, false), ia => resolveAlreadyInstalledPrimaryCTALink(ia).map(cta =>
  /*#__PURE__*/
  // eslint-disable-next-line react/jsx-key
  _jsx(ConfirmCancelFooter, {
    closeInstallDialog: props.closeInstallDialog,
    isLoading: false,
    isPrimaryDisabled: false,
    onPrimaryCTAClick: onClickAlreadyInstalledCTA(iabmu.installableAppByUrlId, cta.label, cta.url),
    primaryCTALabel: cta.label
  })).orElseL(() => resolveConfirmCancelForAppByUrl(iabmu, true)))).getOrElseL(() => /*#__PURE__*/_jsx(ConfirmCancelFooter, {
    closeInstallDialog: props.closeInstallDialog,
    isLoading: false,
    isPrimaryDisabled: true,
    primaryCTALabel: MANUAL_URL_PRIMARY_CTA_LABEL
  })));
};
export default withAnalytics(InstallableAppFooterBase);